/**
 * Comparators for client-side table sorting.
 *
 * The list screens sort server-side (Mongo `.collation({ locale: 'es', strength: 1 })`,
 * see routes/accountRoutes.js). Detail-page tabs receive their rows fully
 * materialized as a prop, so they sort in the browser instead — these helpers
 * keep that ordering consistent with what the backend would have produced.
 */

/** Values that carry no sortable content — they always sort last, in both directions. */
export function isEmptyValue(value) {
	return value === null || value === undefined || value === '';
}

function toTime(value) {
	if (value instanceof Date) {
		const time = value.getTime();
		return Number.isNaN(time) ? null : time;
	}
	// moment objects (and anything else date-like) expose isValid()/valueOf().
	if (value && typeof value === 'object' && typeof value.isValid === 'function' && typeof value.valueOf === 'function') {
		return value.isValid() ? value.valueOf() : null;
	}
	return null;
}

/**
 * Normalizes a raw date field (usually an ISO string off the API) into a Date
 * the comparator can order numerically. Missing or unparseable dates come back
 * as null so `isEmptyValue` sinks those rows to the bottom instead of letting
 * an "Invalid Date" fall through to a string comparison.
 */
export function dateValue(value) {
	if (isEmptyValue(value)) {
		return null;
	}
	const date = value instanceof Date ? value : new Date(value);
	return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * A due's number only lives inside its customId
 * (`${concept} MM-YYYY Nro-${n}`, see duesgenerator.js), so sorting the raw
 * customId string orders by month before year. This pulls the number out —
 * mirroring the backend's `parseNroFromCustomId` — and returns null for
 * manual Efectivo/Eventual dues, which carry no Nro, so they sort last.
 */
export function parseDueNumber(customId) {
	const match = typeof customId === 'string' && customId.match(/Nro-(\d+)/);
	return match ? parseInt(match[1], 10) : null;
}

/**
 * Ascending comparator for two non-empty cell values. Callers handle empties
 * (see `isEmptyValue`) and flip the sign for descending order.
 */
export function compareValues(a, b) {
	if (typeof a === 'number' && typeof b === 'number') {
		return a - b;
	}
	if (typeof a === 'boolean' || typeof b === 'boolean') {
		return Number(a) - Number(b);
	}
	const aTime = toTime(a);
	const bTime = toTime(b);
	if (aTime !== null && bTime !== null) {
		return aTime - bTime;
	}
	// `numeric` is what makes customIds order naturally (D-2 before D-10);
	// `sensitivity: 'base'` mirrors the backend's accent/case-insensitive collation.
	return String(a).localeCompare(String(b), 'es', { numeric: true, sensitivity: 'base' });
}

/**
 * The one toggle rule every sortable table follows, server- or client-sorted:
 * clicking a new column starts it ascending; re-clicking the active column
 * flips it.
 */
export function nextSortDirection(activeColumn, activeDirection, clickedColumn) {
	if (clickedColumn !== activeColumn) {
		return 'asc';
	}
	return activeDirection === 'asc' ? 'desc' : 'asc';
}

function compareWithEmptiesLast(a, b) {
	const aEmpty = isEmptyValue(a);
	const bEmpty = isEmptyValue(b);
	if (aEmpty || bEmpty) {
		return aEmpty && bEmpty ? 0 : aEmpty ? 1 : -1;
	}
	return null;
}

/**
 * Returns a sorted copy of `rows`. Blanks sink to the bottom whichever way
 * the column is sorted. Rows whose values tie fall back to `tieBreaker`
 * (always ascending) so equal dates, amounts, etc. keep a deterministic
 * order instead of whatever order the API happened to return.
 */
export function sortRows(rows, { accessor, direction = 'asc', tieBreaker } = {}) {
	if (!accessor) {
		return rows;
	}
	const sign = direction === 'desc' ? -1 : 1;
	return [...rows].sort((rowA, rowB) => {
		const a = accessor(rowA);
		const b = accessor(rowB);
		const emptyOrder = compareWithEmptiesLast(a, b);
		const primary = emptyOrder !== null ? emptyOrder : compareValues(a, b) * sign;
		if (primary !== 0 || !tieBreaker || tieBreaker === accessor) {
			return primary;
		}
		const tieA = tieBreaker(rowA);
		const tieB = tieBreaker(rowB);
		const tieEmptyOrder = compareWithEmptiesLast(tieA, tieB);
		return tieEmptyOrder !== null ? tieEmptyOrder : compareValues(tieA, tieB);
	});
}
